import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Plus, Send, Trash2, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  WhatsAppCarouselMessageBubble,
  WhatsAppInteractiveMessageBubble,
  WhatsAppPollMessageBubble,
} from "@/components/chat/WhatsAppInteractiveMessageBubble";
import {
  buildFabInteractiveFromButtonForm,
  buildFabInteractiveFromCarouselForm,
  buildFabInteractiveFromListForm,
  buildFabInteractiveFromPollForm,
  buildFabPollListPayload,
  slugifyInteractiveId,
  type FabInteractiveCanonical,
  type WhatsAppCarouselDisplay,
  type WhatsAppInteractiveDisplay,
  type WhatsAppPollDisplay,
  type FabPollMetadata,
} from "@/lib/whatsapp-interactive-message";
import {
  getWhatsAppProviderCapabilities,
  type WhatsAppProviderId,
} from "@/lib/whatsapp-provider-capabilities";

type InteractiveType = "button" | "list" | "poll" | "carousel";

interface ButtonRow {
  title: string;
}

interface ListRow {
  title: string;
  description: string;
}

interface CarouselButtonRow {
  text: string;
  type: "REPLY" | "URL";
  url: string;
}

interface CarouselCardRow {
  text: string;
  imageUrl: string;
  buttons: CarouselButtonRow[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  memberId?: string | null;
  contextMessageId?: string | null;
  provider: WhatsAppProviderId;
}

const emptyButton = (): ButtonRow => ({ title: "" });
const emptyListRow = (): ListRow => ({ title: "", description: "" });
const emptyPollOption = (): string => "";
const emptyCarouselButton = (): CarouselButtonRow => ({ text: "", type: "REPLY", url: "" });
const emptyCarouselCard = (): CarouselCardRow => ({
  text: "",
  imageUrl: "",
  buttons: [emptyCarouselButton()],
});

export function SendWhatsAppInteractiveDialog({
  open,
  onOpenChange,
  conversationId,
  memberId,
  contextMessageId,
  provider,
}: Props) {
  const { toast } = useToast();
  const capabilities = getWhatsAppProviderCapabilities(provider);
  const carouselSupported = capabilities?.carousel === true;
  const [interactiveType, setInteractiveType] = useState<InteractiveType>("poll");
  const [bodyText, setBodyText] = useState("");
  const [buttons, setButtons] = useState<ButtonRow[]>([emptyButton(), emptyButton()]);
  const [menuButton, setMenuButton] = useState("Ver opções");
  const [listRows, setListRows] = useState<ListRow[]>([emptyListRow(), emptyListRow()]);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState<string[]>([
    emptyPollOption(),
    emptyPollOption(),
  ]);
  const [carouselCards, setCarouselCards] = useState<CarouselCardRow[]>([emptyCarouselCard()]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInteractiveType("poll");
    setBodyText("");
    setButtons([emptyButton(), emptyButton()]);
    setMenuButton("Ver opções");
    setListRows([emptyListRow(), emptyListRow()]);
    setPollQuestion("");
    setPollOptions([emptyPollOption(), emptyPollOption()]);
    setCarouselCards([emptyCarouselCard()]);
  }, [open]);

  const canSend = useMemo(() => {
    if (interactiveType === "poll") {
      const valid = pollOptions.map((o) => o.trim()).filter(Boolean);
      return pollQuestion.trim().length > 0 && valid.length >= 2 && valid.length <= 10;
    }
    if (interactiveType === "carousel") {
      if (!bodyText.trim()) return false;
      const validCards = carouselCards.filter((c) => c.text.trim());
      return validCards.length >= 1 && validCards.length <= 10;
    }
    if (!bodyText.trim()) return false;
    if (interactiveType === "button") {
      const valid = buttons.filter((b) => b.title.trim());
      return valid.length >= 1 && valid.length <= 3;
    }
    const validRows = listRows.filter((r) => r.title.trim());
    return menuButton.trim().length > 0 && validRows.length >= 1 && validRows.length <= 10;
  }, [bodyText, interactiveType, buttons, menuButton, listRows, pollQuestion, pollOptions, carouselCards]);

  const previewCarousel = useMemo((): WhatsAppCarouselDisplay | null => {
    if (interactiveType !== "carousel" || !bodyText.trim()) return null;
    const validCards = carouselCards
      .filter((c) => c.text.trim())
      .slice(0, 10)
      .map((card, cardIdx) => {
        const validButtons = card.buttons
          .filter((b) => b.text.trim())
          .slice(0, 3)
          .map((b, i) => ({
            id:
              b.type === "URL"
                ? b.url.trim() || b.text.trim()
                : slugifyInteractiveId(b.text.trim(), i, "btn"),
            text: b.text.trim(),
            type: b.type,
          }));
        return {
          text: card.text.trim(),
          imageUrl: card.imageUrl.trim() || undefined,
          buttons:
            validButtons.length > 0
              ? validButtons
              : [{ id: slugifyInteractiveId("Ver mais", cardIdx, "btn"), text: "Ver mais", type: "REPLY" }],
        };
      });
    if (validCards.length === 0) return null;
    return { bodyText: bodyText.trim(), cards: validCards };
  }, [bodyText, interactiveType, carouselCards]);

  const previewInteractive = useMemo((): WhatsAppInteractiveDisplay | null => {
    if (interactiveType === "poll" || interactiveType === "carousel") return null;
    if (!bodyText.trim()) return null;
    if (interactiveType === "button") {
      const validButtons = buttons
        .filter((b) => b.title.trim())
        .slice(0, 3)
        .map((b, i) => ({
          id: slugifyInteractiveId(b.title.trim(), i, "btn"),
          title: b.title.trim(),
        }));
      if (validButtons.length === 0) return null;
      return { type: "button", bodyText: bodyText.trim(), buttons: validButtons };
    }
    const validRows = listRows
      .filter((r) => r.title.trim())
      .slice(0, 10)
      .map((r, i) => ({
        id: slugifyInteractiveId(r.title.trim(), i, "row"),
        title: r.title.trim(),
        ...(r.description.trim() ? { description: r.description.trim() } : {}),
      }));
    if (validRows.length === 0 || !menuButton.trim()) return null;
    return {
      type: "list",
      bodyText: bodyText.trim(),
      menuButton: menuButton.trim(),
      listRows: validRows,
    };
  }, [bodyText, interactiveType, buttons, menuButton, listRows]);

  const previewPoll = useMemo((): WhatsAppPollDisplay | null => {
    if (interactiveType !== "poll") return null;
    const question = pollQuestion.trim();
    const options = pollOptions
      .map((o) => o.trim())
      .filter(Boolean)
      .slice(0, 10)
      .map((title, i) => ({
        id: slugifyInteractiveId(title, i, "row"),
        title,
      }));
    if (!question || options.length < 2) return null;
    return { question, options, allowMultiple: false };
  }, [interactiveType, pollQuestion, pollOptions]);

  const buildPayload = (): {
    fab_interactive: FabInteractiveCanonical;
    interactive?: Record<string, unknown>;
    fab_poll?: FabPollMetadata;
  } => {
    if (interactiveType === "carousel") {
      const fab_interactive = buildFabInteractiveFromCarouselForm(
        bodyText,
        carouselCards.map((card) => ({
          text: card.text,
          imageUrl: card.imageUrl,
          buttons: card.buttons
            .filter((b) => b.text.trim())
            .map((b) => ({
              text: b.text,
              type: b.type,
              id: b.type === "URL" ? b.url.trim() || undefined : undefined,
            })),
        })),
      );
      return { fab_interactive };
    }

    if (interactiveType === "poll") {
      const validOptions = pollOptions.map((o) => o.trim()).filter(Boolean).slice(0, 10);
      const fab_interactive = buildFabInteractiveFromPollForm(pollQuestion, validOptions);
      if (provider === "whatsapp_cloud") {
        const cloudPayload = buildFabPollListPayload(pollQuestion, validOptions);
        return { fab_interactive, interactive: cloudPayload.interactive, fab_poll: cloudPayload.fab_poll };
      }
      return { fab_interactive };
    }

    if (interactiveType === "button") {
      const fab_interactive = buildFabInteractiveFromButtonForm(bodyText, buttons);
      if (provider === "whatsapp_cloud") {
        const validButtons = buttons
          .filter((b) => b.title.trim())
          .slice(0, 3)
          .map((b, i) => ({
            type: "reply" as const,
            reply: {
              id: slugifyInteractiveId(b.title.trim(), i, "btn"),
              title: b.title.trim(),
            },
          }));
        return {
          fab_interactive,
          interactive: {
            type: "button" as const,
            body: { text: bodyText.trim() },
            action: { buttons: validButtons },
          },
        };
      }
      return { fab_interactive };
    }

    const fab_interactive = buildFabInteractiveFromListForm(bodyText, menuButton, listRows);
    if (provider === "whatsapp_cloud") {
      const validRows = listRows
        .filter((r) => r.title.trim())
        .slice(0, 10)
        .map((r, i) => ({
          id: slugifyInteractiveId(r.title.trim(), i, "row"),
          title: r.title.trim(),
          ...(r.description.trim() ? { description: r.description.trim() } : {}),
        }));
      return {
        fab_interactive,
        interactive: {
          type: "list" as const,
          body: { text: bodyText.trim() },
          action: {
            button: menuButton.trim(),
            sections: [{ title: "Opções", rows: validRows }],
          },
        },
      };
    }
    return { fab_interactive };
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);

    const payload = buildPayload();
    const edgeFn =
      provider === "whatsapp_uazapi"
        ? "whatsapp-uazapi-send-interactive"
        : "whatsapp-cloud-send-interactive";

    if (interactiveType === "carousel" && provider !== "whatsapp_uazapi") {
      toast({
        title: "Carrossel indisponível",
        description: "O carrossel interativo está disponível apenas via UAZAPI.",
        variant: "destructive",
      });
      setSending(false);
      return;
    }

    const { data, error } = await supabase.functions.invoke(edgeFn, {
      body: {
        conversation_id: conversationId,
        member_id: memberId || undefined,
        context_message_id: contextMessageId || undefined,
        fab_interactive: payload.fab_interactive,
        ...(payload.interactive ? { interactive: payload.interactive } : {}),
        ...(payload.fab_poll ? { fab_poll: payload.fab_poll } : {}),
      },
    });

    setSending(false);

    if (error || (data as any)?.error) {
      toast({
        title: "Falha ao enviar",
        description:
          (data as any)?.friendly_message ||
          (data as any)?.error ||
          error?.message ||
          "Erro desconhecido",
        variant: "destructive",
      });
      return;
    }

    if ((data as any)?.success === false) {
      toast({
        title: "Não foi possível enviar",
        description: (data as any)?.friendly_message || "Tente novamente.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title:
        interactiveType === "poll"
          ? "Enquete enviada"
          : interactiveType === "carousel"
            ? "Carrossel enviado"
            : "Mensagem interativa enviada",
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enviar interativo</DialogTitle>
          <DialogDescription>
            {provider === "whatsapp_cloud"
              ? "Botões, lista ou enquete dentro da janela de 24h (WhatsApp Cloud)."
              : carouselSupported
                ? "Botões, lista, enquete ou carrossel nativo via WhatsApp Business."
                : "Botões, lista ou enquete nativa via WhatsApp Business."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Tabs
            value={interactiveType}
            onValueChange={(v) => setInteractiveType(v as InteractiveType)}
          >
            <TabsList className={cn("grid w-full", carouselSupported ? "grid-cols-4" : "grid-cols-3")}>
              <TabsTrigger value="poll">Enquete</TabsTrigger>
              <TabsTrigger value="button">Botões</TabsTrigger>
              <TabsTrigger value="list">Lista</TabsTrigger>
              {carouselSupported && <TabsTrigger value="carousel">Carrossel</TabsTrigger>}
            </TabsList>

            <TabsContent value="poll" className="mt-4 space-y-4">
              {provider === "whatsapp_cloud" ? (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    A API Cloud da Meta não tem enquete nativa. Enviamos como lista interativa com
                    botão &quot;Votar&quot; — o contato escolhe <strong>uma opção</strong>. Enquetes
                    enviadas pelo app WhatsApp do cliente não chegam pela API.
                  </AlertDescription>
                </Alert>
              ) : capabilities?.nativePoll ? (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Enquete nativa do WhatsApp — o contato pode votar diretamente no app.
                  </AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Pergunta
                </Label>
                <Input
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.target.value)}
                  placeholder="Pergunte algo"
                  maxLength={1024}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Opções
                  </Label>
                  {pollOptions.length < 10 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-primary"
                      onClick={() => setPollOptions((prev) => [...prev, emptyPollOption()])}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      Adicionar opção
                    </Button>
                  )}
                </div>

                {pollOptions.map((opt, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      className="flex-1"
                      placeholder={`Opção ${idx + 1}`}
                      value={opt}
                      onChange={(e) =>
                        setPollOptions((prev) =>
                          prev.map((o, i) => (i === idx ? e.target.value : o)),
                        )
                      }
                      maxLength={24}
                    />
                    {pollOptions.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        onClick={() => setPollOptions((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="button" className="mt-4 space-y-3">
              <div className="space-y-1">
                <Label>Texto da mensagem</Label>
                <Textarea
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  placeholder="Corpo da mensagem..."
                  rows={3}
                  maxLength={1024}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Até 3 botões de resposta</Label>
                {buttons.length < 3 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setButtons((prev) => [...prev, emptyButton()])}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Botão
                  </Button>
                )}
              </div>
              {buttons.map((btn, idx) => (
                <div key={idx} className="flex gap-2 items-start">
                  <Input
                    className="flex-1"
                    placeholder={`Título do botão ${idx + 1}`}
                    value={btn.title}
                    onChange={(e) =>
                      setButtons((prev) =>
                        prev.map((b, i) => (i === idx ? { ...b, title: e.target.value } : b)),
                      )
                    }
                    maxLength={20}
                  />
                  {buttons.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={() => setButtons((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </TabsContent>

            <TabsContent value="list" className="mt-4 space-y-3">
              <div className="space-y-1">
                <Label>Texto da mensagem</Label>
                <Textarea
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  placeholder="Corpo da mensagem..."
                  rows={3}
                  maxLength={1024}
                />
              </div>

              <div className="space-y-1">
                <Label>Texto do botão do menu</Label>
                <Input
                  value={menuButton}
                  onChange={(e) => setMenuButton(e.target.value)}
                  maxLength={20}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Até 10 opções na lista</Label>
                {listRows.length < 10 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setListRows((prev) => [...prev, emptyListRow()])}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Opção
                  </Button>
                )}
              </div>
              {listRows.map((row, idx) => (
                <div key={idx} className="space-y-2 rounded border p-2">
                  <div className="flex gap-2">
                    <Input
                      className="flex-1"
                      placeholder={`Título da opção ${idx + 1}`}
                      value={row.title}
                      onChange={(e) =>
                        setListRows((prev) =>
                          prev.map((r, i) => (i === idx ? { ...r, title: e.target.value } : r)),
                        )
                      }
                      maxLength={24}
                    />
                    {listRows.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        onClick={() => setListRows((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <Input
                    placeholder="Descrição (opcional)"
                    value={row.description}
                    onChange={(e) =>
                      setListRows((prev) =>
                        prev.map((r, i) => (i === idx ? { ...r, description: e.target.value } : r)),
                      )
                    }
                    maxLength={72}
                  />
                </div>
              ))}
            </TabsContent>

            {carouselSupported && (
              <TabsContent value="carousel" className="mt-4 space-y-3">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Carrossel com cartões deslizáveis — cada cartão pode ter imagem (URL pública) e
                    botões opcionais. Se nenhum botão for informado, usamos &quot;Ver mais&quot;.
                  </AlertDescription>
                </Alert>

                <div className="space-y-1">
                  <Label>Texto principal</Label>
                  <Textarea
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value)}
                    placeholder="Ex.: Conheça nossos produtos em destaque"
                    rows={2}
                    maxLength={1024}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Até 10 cartões</Label>
                  {carouselCards.length < 10 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCarouselCards((prev) => [...prev, emptyCarouselCard()])}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      Cartão
                    </Button>
                  )}
                </div>

                {carouselCards.map((card, cardIdx) => (
                  <div key={cardIdx} className="space-y-2 rounded border p-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium">Cartão {cardIdx + 1}</Label>
                      {carouselCards.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() =>
                            setCarouselCards((prev) => prev.filter((_, i) => i !== cardIdx))
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <Input
                      placeholder="URL da imagem (opcional)"
                      value={card.imageUrl}
                      onChange={(e) =>
                        setCarouselCards((prev) =>
                          prev.map((c, i) => (i === cardIdx ? { ...c, imageUrl: e.target.value } : c)),
                        )
                      }
                    />

                    <Textarea
                      placeholder="Texto do cartão"
                      value={card.text}
                      onChange={(e) =>
                        setCarouselCards((prev) =>
                          prev.map((c, i) => (i === cardIdx ? { ...c, text: e.target.value } : c)),
                        )
                      }
                      rows={2}
                      maxLength={1024}
                    />

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Botões (opcional)</Label>
                        {card.buttons.length < 3 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() =>
                              setCarouselCards((prev) =>
                                prev.map((c, i) =>
                                  i === cardIdx
                                    ? { ...c, buttons: [...c.buttons, emptyCarouselButton()] }
                                    : c,
                                ),
                              )
                            }
                          >
                            <Plus className="mr-1 h-3 w-3" />
                            Botão
                          </Button>
                        )}
                      </div>

                      {card.buttons.map((btn, btnIdx) => (
                        <div key={btnIdx} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <Input
                            className="flex-1"
                            placeholder="Texto do botão"
                            value={btn.text}
                            onChange={(e) =>
                              setCarouselCards((prev) =>
                                prev.map((c, i) =>
                                  i === cardIdx
                                    ? {
                                        ...c,
                                        buttons: c.buttons.map((b, j) =>
                                          j === btnIdx ? { ...b, text: e.target.value } : b,
                                        ),
                                      }
                                    : c,
                                ),
                              )
                            }
                            maxLength={20}
                          />
                          <select
                            className="h-9 rounded-md border bg-background px-2 text-sm"
                            value={btn.type}
                            onChange={(e) =>
                              setCarouselCards((prev) =>
                                prev.map((c, i) =>
                                  i === cardIdx
                                    ? {
                                        ...c,
                                        buttons: c.buttons.map((b, j) =>
                                          j === btnIdx
                                            ? { ...b, type: e.target.value as "REPLY" | "URL" }
                                            : b,
                                        ),
                                      }
                                    : c,
                                ),
                              )
                            }
                          >
                            <option value="REPLY">Resposta</option>
                            <option value="URL">Link</option>
                          </select>
                          {btn.type === "URL" && (
                            <Input
                              className="flex-1"
                              placeholder="URL do link"
                              value={btn.url}
                              onChange={(e) =>
                                setCarouselCards((prev) =>
                                  prev.map((c, i) =>
                                    i === cardIdx
                                      ? {
                                          ...c,
                                          buttons: c.buttons.map((b, j) =>
                                            j === btnIdx ? { ...b, url: e.target.value } : b,
                                          ),
                                        }
                                      : c,
                                  ),
                                )
                              }
                            />
                          )}
                          {card.buttons.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="shrink-0"
                              onClick={() =>
                                setCarouselCards((prev) =>
                                  prev.map((c, i) =>
                                    i === cardIdx
                                      ? {
                                          ...c,
                                          buttons: c.buttons.filter((_, j) => j !== btnIdx),
                                        }
                                      : c,
                                  ),
                                )
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </TabsContent>
            )}
          </Tabs>

          {(previewPoll || previewInteractive || previewCarousel) && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Pré-visualização</Label>
              <div className="rounded-lg bg-[#0b141a] p-3">
                {previewPoll ? (
                  <WhatsAppPollMessageBubble poll={previewPoll} variant="preview" />
                ) : previewCarousel ? (
                  <WhatsAppCarouselMessageBubble carousel={previewCarousel} variant="preview" />
                ) : previewInteractive ? (
                  <WhatsAppInteractiveMessageBubble
                    interactive={previewInteractive}
                    variant="preview"
                  />
                ) : null}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={!canSend || sending}>
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SendWhatsAppInteractiveDialog;
